import { Ban, Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { GradientColorStop, GradientConfig, isGradientConfig } from "../models/Post";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import {
  BrandColorPickerSwatch,
  BrandColorShortcuts,
  type BrandKitColorPickerContext,
  type BrandColorShortcut,
} from "./BrandColorShortcuts";
import { HexColorInput } from "./HexColorInput";

type ColorMode = "solid" | "gradient";

/**
 * Color stop row with inline color picker that supports transparent option
 */
function ColorStopRow({
  stop,
  onColorChange,
  onOffsetChange,
  onOpacityChange,
  onRemove,
  brandColors,
  brandKitContext,
}: {
  stop: GradientColorStop;
  onColorChange: (color: string) => void;
  onOffsetChange: (offset: number) => void;
  onOpacityChange: (opacity: number) => void;
  onRemove?: () => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isTransparent = stop.color === "transparent";
  const opacity = stop.opacity ?? 1;

  // Check if a string is a valid hex color
  const isValidHex = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

  // Swatch shows the actual rendered color including opacity, layered over a checkerboard
  const colorWithAlpha =
    opacity < 1 && isValidHex(stop.color)
      ? `${stop.color}${Math.round(opacity * 255).toString(16).padStart(2, "0").toUpperCase()}`
      : stop.color;
  const checkerboard =
    "repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 8px 8px";
  const swatchBackground = isTransparent
    ? checkerboard
    : opacity < 1
    ? `linear-gradient(${colorWithAlpha}, ${colorWithAlpha}), ${checkerboard}`
    : stop.color;

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        {/* Color swatch button — checkerboard behind, color (with alpha) on top */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-10 h-8 rounded border flex-shrink-0 flex items-center justify-center overflow-hidden bg-clip-padding hover:ring-2 hover:ring-inset hover:ring-primary/50 transition-all"
          style={{ background: swatchBackground }}
        >
          {isTransparent && (
            <Ban className="w-5 h-5 text-red-500" strokeWidth={2.5} />
          )}
        </button>

        {/* Dropdown color picker */}
        {isOpen && (
          <>
            {/* Backdrop to close on click outside */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute left-0 top-full mt-1 z-50 bg-background border rounded-md shadow-lg p-3 space-y-3 w-56">
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={isTransparent ? "#888888" : (isValidHex(stop.color) ? stop.color : "#888888")}
                  onChange={(e) => onColorChange(e.target.value)}
                  className="w-12 h-10 p-1"
                />
                <HexColorInput
                  value={isTransparent ? "" : stop.color}
                  onChange={onColorChange}
                  className="flex-1 text-sm"
                  aria-label="Color stop hex"
                />
              </div>
              <BrandColorShortcuts
                colors={brandColors}
                onSelect={onColorChange}
                brandKitContext={brandKitContext}
                size="sm"
              />
              {/* Opacity slider — only meaningful for actual colors, not the "Transparent" sentinel */}
              {!isTransparent && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground w-14 shrink-0">Opacity</Label>
                  <Slider
                    value={[Math.round(opacity * 100)]}
                    onValueChange={([v]) => onOpacityChange(v / 100)}
                    min={0}
                    max={100}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-xs text-muted-foreground w-9 text-right tabular-nums">
                    {Math.round(opacity * 100)}%
                  </span>
                </div>
              )}
              <Button
                variant={isTransparent ? "default" : "outline"}
                size="sm"
                className="w-full"
                onClick={() => {
                  onColorChange("transparent");
                  setIsOpen(false);
                }}
              >
                <Ban className="w-4 h-4 mr-2" />
                Transparent
              </Button>
            </div>
          </>
        )}
      </div>

      <Slider
        value={[stop.offset * 100]}
        onValueChange={([v]) => onOffsetChange(v / 100)}
        min={0}
        max={100}
        step={1}
        className="flex-1"
      />
      <span className="text-xs text-muted-foreground w-8">
        {Math.round(stop.offset * 100)}%
      </span>
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={onRemove}
        >
          <Trash2 className="w-3 h-3 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}

interface GradientPickerProps {
  value: string | GradientConfig | undefined;
  onChange: (value: string | GradientConfig) => void;
  label?: string;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}

export function GradientPicker({
  value,
  onChange,
  label = "Color",
  brandColors,
  brandKitContext,
}: GradientPickerProps) {
  const isGradient = isGradientConfig(value);
  const mode: ColorMode = isGradient ? "gradient" : "solid";
  
  const solidColor = useMemo(() => {
    if (isGradient) {
      return value.colorStops[0]?.color ?? "#000000";
    }
    return value ?? "#000000";
  }, [value, isGradient]);
  
  const gradientConfig = useMemo((): GradientConfig => {
    if (isGradient) {
      return value;
    }
    // Convert solid color to default gradient using the solid color
    return {
      type: "linear",
      angle: 90,
      colorStops: [
        { offset: 0, color: solidColor },
        { offset: 1, color: "#FFFFFF" },
      ],
    };
  }, [value, isGradient, solidColor]);

  const handleSwitchToSolid = useCallback(() => {
    // Switch to solid - use first color stop from gradient, or keep current solid
    if (isGradientConfig(value)) {
      onChange(value.colorStops[0]?.color ?? "#000000");
    }
    // If already solid, do nothing
  }, [value, onChange]);

  const handleSwitchToGradient = useCallback(() => {
    // Switch to gradient - create a new gradient config
    if (!isGradientConfig(value)) {
      // Convert solid to gradient
      const currentColor = value ?? "#000000";
      onChange({
        type: "linear",
        angle: 90,
        colorStops: [
          { offset: 0, color: currentColor },
          { offset: 1, color: "#FFFFFF" },
        ],
      });
    }
    // If already gradient, do nothing
  }, [value, onChange]);

  const handleSolidColorChange = useCallback((color: string) => {
    onChange(color);
  }, [onChange]);

  const handleGradientTypeChange = useCallback((type: "linear" | "radial") => {
    if (isGradient) {
      onChange({ ...value, type });
    }
  }, [isGradient, value, onChange]);

  const handleAngleChange = useCallback((angle: number) => {
    if (isGradient) {
      onChange({ ...value, angle });
    }
  }, [isGradient, value, onChange]);

  const handleColorStopChange = useCallback((index: number, updates: Partial<GradientColorStop>) => {
    if (isGradient) {
      const newStops = [...value.colorStops];
      newStops[index] = { ...newStops[index], ...updates };
      onChange({ ...value, colorStops: newStops });
    }
  }, [isGradient, value, onChange]);

  const handleAddColorStop = useCallback(() => {
    if (isGradient && value.colorStops.length < 5) {
      // Add a new stop in the middle
      const lastStop = value.colorStops[value.colorStops.length - 1];
      const newOffset = Math.min(lastStop.offset + 0.25, 1);
      const newStops = [...value.colorStops, { offset: newOffset, color: "#888888" }];
      // Sort by offset
      newStops.sort((a, b) => a.offset - b.offset);
      onChange({ ...value, colorStops: newStops });
    }
  }, [isGradient, value, onChange]);

  const handleRemoveColorStop = useCallback((index: number) => {
    if (isGradient && value.colorStops.length > 2) {
      const newStops = value.colorStops.filter((_, i) => i !== index);
      onChange({ ...value, colorStops: newStops });
    }
  }, [isGradient, value, onChange]);

  // Generate CSS gradient for preview — bake per-stop opacity into the color string
  const gradientPreviewStyle = useMemo(() => {
    if (!isGradient) return {};

    const stops = value.colorStops
      .map((stop) => {
        const op = stop.opacity ?? 1;
        let cssColor = stop.color;
        // Append 2-digit hex alpha if opacity < 1 and color is a 6-digit hex
        if (op < 1 && /^#[0-9A-Fa-f]{6}$/.test(stop.color)) {
          const a = Math.round(op * 255).toString(16).padStart(2, "0");
          cssColor = `${stop.color}${a}`;
        }
        return `${cssColor} ${stop.offset * 100}%`;
      })
      .join(", ");

    if (value.type === "linear") {
      return { background: `linear-gradient(${value.angle ?? 90}deg, ${stops})` };
    } else {
      return { background: `radial-gradient(circle, ${stops})` };
    }
  }, [isGradient, value]);

  return (
    <div className="space-y-3">
      {/* Mode Toggle - Using buttons for reliability */}
      <div className="flex items-center justify-between">
        {label && <Label>{label}</Label>}
        <div className="flex rounded-md border overflow-hidden">
          <button
            type="button"
            onClick={handleSwitchToSolid}
            className={cn(
              "px-3 py-1 text-sm transition-colors",
              mode === "solid" 
                ? "bg-primary text-primary-foreground" 
                : "bg-background hover:bg-muted"
            )}
          >
            Solid
          </button>
          <button
            type="button"
            onClick={handleSwitchToGradient}
            className={cn(
              "px-3 py-1 text-sm transition-colors border-l",
              mode === "gradient" 
                ? "bg-primary text-primary-foreground" 
                : "bg-background hover:bg-muted"
            )}
          >
            Gradient
          </button>
        </div>
      </div>

      {mode === "solid" ? (
        /* Solid Color Picker */
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <BrandColorPickerSwatch
              value={solidColor}
              onChange={handleSolidColorChange}
              className="h-10 w-10"
              brandColors={brandColors}
              brandKitContext={brandKitContext}
              ariaLabel="Choose fill color"
            />
            <HexColorInput
              value={solidColor}
              onChange={handleSolidColorChange}
              className="flex-1 text-sm"
              aria-label="Fill hex color"
            />
          </div>
        </div>
      ) : (
        /* Gradient Editor */
        <div className="space-y-4">
          {/* Gradient Preview - with checkerboard behind to show transparency */}
          <div 
            className="h-8 rounded-md border relative overflow-hidden"
            style={{
              background: `repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 12px 12px`
            }}
          >
            <div 
              className="absolute inset-0"
              style={gradientPreviewStyle}
            />
          </div>

          {/* Gradient Type */}
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground w-16">Type</Label>
            <div className="flex rounded-md border overflow-hidden flex-1">
              <button
                type="button"
                onClick={() => handleGradientTypeChange("linear")}
                className={cn(
                  "flex-1 px-3 py-1 text-sm transition-colors",
                  gradientConfig.type === "linear" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-background hover:bg-muted"
                )}
              >
                Linear
              </button>
              <button
                type="button"
                onClick={() => handleGradientTypeChange("radial")}
                className={cn(
                  "flex-1 px-3 py-1 text-sm transition-colors border-l",
                  gradientConfig.type === "radial" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-background hover:bg-muted"
                )}
              >
                Radial
              </button>
            </div>
          </div>

          {/* Angle (for linear gradients) */}
          {gradientConfig.type === "linear" && (
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground w-16">Angle</Label>
              <Slider
                value={[gradientConfig.angle ?? 90]}
                onValueChange={([v]) => handleAngleChange(v)}
                min={0}
                max={360}
                step={1}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-10 text-right">
                {gradientConfig.angle ?? 90}°
              </span>
            </div>
          )}

          {/* Color Stops */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Color Stops</Label>
              {gradientConfig.colorStops.length < 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={handleAddColorStop}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Button>
              )}
            </div>
            
            {gradientConfig.colorStops.map((stop, index) => (
              <ColorStopRow
                key={index}
                stop={stop}
                onColorChange={(color) => handleColorStopChange(index, { color })}
                onOffsetChange={(offset) => handleColorStopChange(index, { offset })}
                onOpacityChange={(opacity) => handleColorStopChange(index, { opacity })}
                onRemove={gradientConfig.colorStops.length > 2 ? () => handleRemoveColorStop(index) : undefined}
                brandColors={brandColors}
                brandKitContext={brandKitContext}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
